import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import type { Template } from "../../../services/api";
import { useLocale } from "../../../locales";

interface TemplatesModalProps {
  templates: Template[];
  onClose: () => void;
  onSelect: (template: Template) => void;
}

const TemplatesModal: React.FC<TemplatesModalProps> = ({
  templates,
  onClose,
  onSelect,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const normalize = useCallback(
    (value: string) => value.trim().toLocaleLowerCase("tr-TR"),
    []
  );

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [onClose]);

  const resolveTemplateTags = useCallback((template: Template): string[] => {
    const rawValue =
      (template as any)?.tags ??
      (template as any)?.tag ??
      (template as any)?.question?.tags;
    const values: string[] = [];

    if (Array.isArray(rawValue)) {
      rawValue.forEach((item) => {
        if (typeof item === "string") {
          values.push(item);
        } else if (item && typeof item === "object") {
          const label = (item as any).label ?? (item as any).name;
          if (typeof label === "string") {
            values.push(label);
          }
        }
      });
    } else if (typeof rawValue === "string") {
      rawValue
        .split(/[;,]+/)
        .map((tag) => tag.trim())
        .filter(Boolean)
        .forEach((tag) => values.push(tag));
    }

    const map = new Map<string, string>();
    values.forEach((tag) => {
      const trimmed = tag.trim();
      if (!trimmed) return;
      const key = normalize(trimmed);
      if (!map.has(key)) {
        map.set(key, trimmed);
      }
    });
    return Array.from(map.values());
  }, [normalize]);

  const templatesWithTags = useMemo(
    () =>
      templates.map((template) => ({
        template,
        tags: resolveTemplateTags(template),
      })),
    [templates, resolveTemplateTags]
  );

  const normalizedQuery = useMemo(
    () => normalize(searchQuery),
    [normalize, searchQuery]
  );

  const matchingTagKeys = useMemo(() => {
    if (!normalizedQuery) return [];
    const keys = new Set<string>();
    templatesWithTags.forEach(({ tags }) => {
      tags.forEach((tag) => {
        const tagKey = normalize(tag);
        if (tagKey.includes(normalizedQuery)) {
          keys.add(tagKey);
        }
      });
    });
    return Array.from(keys.values());
  }, [normalize, normalizedQuery, templatesWithTags]);

  const visibleTemplates = useMemo(() => {
    if (!normalizedQuery) {
      return templatesWithTags;
    }
    if (matchingTagKeys.length > 0) {
      return templatesWithTags.filter(({ tags }) =>
        tags.some((tag) => matchingTagKeys.includes(normalize(tag)))
      );
    }
    return templatesWithTags.filter(({ template, tags }) => {
      const searchableTitle = template.title || t("templatesModal.fallbackName");
      const titleMatch = normalize(searchableTitle).includes(normalizedQuery);
      const tagMatch = tags.some((tag) =>
        normalize(tag).includes(normalizedQuery)
      );
      return titleMatch || tagMatch;
    });
  }, [matchingTagKeys, normalize, normalizedQuery, templatesWithTags]);

  const groupedTemplates = useMemo(() => {
    const groups = new Map<string, { label: string; items: Template[] }>();
    const untagged: Template[] = [];

    visibleTemplates.forEach(({ template, tags }) => {
      if (tags.length === 0) {
        untagged.push(template);
        return;
      }
      const filteredTags =
        matchingTagKeys.length > 0
          ? tags.filter((tag) => matchingTagKeys.includes(normalize(tag)))
          : tags;
      filteredTags.forEach((tag) => {
        const key = normalize(tag);
        const existing = groups.get(key);
        if (existing) {
          existing.items.push(template);
        } else {
          groups.set(key, { label: tag, items: [template] });
        }
      });
    });

    const sorted = Array.from(groups.entries())
      .map(([key, value]) => ({
        key,
        label: value.label,
        items: value.items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr-TR"));

    return { groups: sorted, untagged };
  }, [matchingTagKeys, normalize, visibleTemplates]);

  const handleSelect = useCallback(
    (template: Template) => {
      onSelect(template);
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div
        className="qe-modal__content qe-modal__content--media-library"
        ref={contentRef}
        tabIndex={-1}
      >
        <div className="qe-modal__header">
          <div className="qe-modal__header-content">
            <h2 className="qe-modal__title">{t("templatesModal.title")}</h2>
            <p className="qe-modal__subtitle">
              {t("templatesModal.subtitle")}
            </p>
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t("templatesModal.close")}
          >
            ✕
          </button>
        </div>

        <div className="qe-modal__body">
          <div className="qe-media-library__search">
            <input
              type="text"
              className="qe-input"
              placeholder={t("templatesModal.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {visibleTemplates.length === 0 ? (
            <div className="qe-media-library__empty">
              <p>
                {searchQuery ? t("templatesModal.noSearchResults") : t("templatesModal.noTemplates")}
              </p>
            </div>
          ) : (
            <div className="qe-media-library__groups">
              {groupedTemplates.groups.map((group) => (
                <div key={group.key} className="qe-media-library__group">
                  <div className="qe-media-library__group-title">
                    {group.label}
                  </div>
                  <div className="qe-media-library__grid">
                    {group.items.map((template) => {
                      const templateId = String(template.id);
                      const tags = resolveTemplateTags(template);
                      return (
                        <div
                          key={templateId}
                          className="qe-media-library__item"
                          onClick={() => handleSelect(template)}
                          style={{ cursor: "pointer" }}
                        >
                          {template.thumbnail || template.screenshotFile?.url ? (
                            <div className="qe-media-library__item-image">
                              <img
                                src={
                                  template.screenshotFile?.url ||
                                  template.thumbnail
                                }
                                alt={template.title || t("templatesModal.altFallback")}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              className="qe-media-library__item-image"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f3f4f6",
                                color: "#9ca3af",
                                fontSize: "48px",
                              }}
                            >
                              📄
                            </div>
                          )}
                          <div className="qe-media-library__item-name">
                            {template?.title || t("templatesModal.fallbackName")}
                          </div>
                          {tags.length > 0 && (
                            <div className="qe-template-tags">
                              {tags.slice(0, 3).map((tag) => (
                                <span key={tag} className="qe-template-tag">
                                  {tag}
                                </span>
                              ))}
                              {tags.length > 3 && (
                                <span className="qe-template-tag qe-template-tag--more">
                                  +{tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          {template.description && (
                            <div className="qe-media-library__item-size">
                              {template.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {groupedTemplates.untagged.length > 0 && (
                <div className="qe-media-library__group">
                  <div className="qe-media-library__group-title">
                    {t("templatesModal.noTags")}
                  </div>
                  <div className="qe-media-library__grid">
                    {groupedTemplates.untagged.map((template) => {
                      const templateId = String(template.id);
                      return (
                        <div
                          key={templateId}
                          className="qe-media-library__item"
                          onClick={() => handleSelect(template)}
                          style={{ cursor: "pointer" }}
                        >
                          {template.thumbnail || template.screenshotFile?.url ? (
                            <div className="qe-media-library__item-image">
                              <img
                                src={
                                  template.screenshotFile?.url ||
                                  template.thumbnail
                                }
                                alt={template.title || t("templatesModal.altFallback")}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              className="qe-media-library__item-image"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f3f4f6",
                                color: "#9ca3af",
                                fontSize: "48px",
                              }}
                            >
                              📄
                            </div>
                          )}
                          <div className="qe-media-library__item-name">
                            {template?.title || t("templatesModal.fallbackName")}
                          </div>
                          {template.description && (
                            <div className="qe-media-library__item-size">
                              {template.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="qe-modal__footer">
          <button
            type="button"
            className="qe-button qe-button--secondary"
            onClick={onClose}
          >
            {t("templatesModal.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatesModal;
